import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "./axiosConfig";
import Updates from "./Updates";
import Profile from "./Profile";
import Menu from "./Menu";
import NavigationButtons from "./NavigationButtons";
import MainContent from "./MainContent";
import { generateBackground } from "./background";
import FloatingButton from "./components/FloatingButton";
import PostPopup from "./components/PostPopup";
import './colors.css';

const USER_RADIUS = 100; // Visibility radius
const PLANE_SIZE = 2000; // Hardcoded plane size
const BOTTOM_TAB_HEIGHT = 60; // Height of the bottom tab


const SocialMedia2DPOC = () => {
  const [planeSize, setPlaneSize] = useState(PLANE_SIZE);
  const [userPosition, setUserPosition] = useState({ x: planeSize / 2, y: planeSize / 2 });
  const [users, setUsers] = useState([]);
  const [visibleUsers, setVisibleUsers] = useState([]);
  const [viewportPosition, setViewportPosition] = useState({ x: 0, y: 0 });
  const [backgroundImage, setBackgroundImage] = useState('');
  const [activeTab, setActiveTab] = useState("World");
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isPostPopupOpen, setIsPostPopupOpen] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      const newSize = PLANE_SIZE;
      setPlaneSize(newSize);
      setUserPosition({ x: newSize / 2, y: newSize / 2 });
      setBackgroundImage(generateBackground(newSize, newSize));
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial call to set background and check screen size
  }, []);

  useEffect(() => {
    // Fetch initial users from the backend when the app loads
    axiosInstance.get("/find/users")
      .then(response => {
        const usersData = response.data;
        const generatedUsers = usersData.map(user => ({
          id: user.id,
          x: user.xpos,
          y: user.ypos,
          name: `User ${user.id}`,
        }));
        setUsers(generatedUsers);
      })
      .catch(error => {
        console.error("There was an error fetching the users!", error);
      });
  }, []);

  useEffect(() => {
    // Recalculate visible users whenever users or userPosition changes
    const calculateVisibleUsers = () => {
      const filteredUsers = users.filter((user) => {
        const distance = Math.sqrt(
          (user.x - userPosition.x) ** 2 + (user.y - userPosition.y) ** 2
        );
        return distance <= USER_RADIUS;
      });
      setVisibleUsers(filteredUsers);
    };

    calculateVisibleUsers();
  }, [users, userPosition]);

  useEffect(() => {
    // Center the viewport on the user when the screen is loaded for the first time
    setViewportPosition({
      x: Math.max(0, Math.min(userPosition.x - window.innerWidth / 2, planeSize - window.innerWidth)),
      y: Math.max(0, Math.min(userPosition.y - window.innerHeight / 2, planeSize - window.innerHeight + BOTTOM_TAB_HEIGHT)),
    });
  }, [userPosition, planeSize]);

  const moveUser = useCallback((direction) => {
    setUserPosition((prev) => {
      const delta = 20;
      let newX = prev.x;
      let newY = prev.y;

      if (direction === "up") newY = Math.max(0, prev.y - delta);
      if (direction === "down") newY = Math.min(planeSize - BOTTOM_TAB_HEIGHT, prev.y + delta);
      if (direction === "left") newX = Math.max(0, prev.x - delta);
      if (direction === "right") newX = Math.min(planeSize, prev.x + delta);

      // Adjust viewport position
      setViewportPosition({
        x: Math.max(0, Math.min(newX - window.innerWidth / 2, planeSize - window.innerWidth)),
        y: Math.max(0, Math.min(newY - window.innerHeight / 2, planeSize - window.innerHeight + BOTTOM_TAB_HEIGHT)),
      });

      // Make API call to update user position
      axiosInstance.post('/api/update', {
        userId,
        xpos: newX,
        ypos: newY
      }).catch(error => {
        console.error('Error updating user position:', error);
      });

      return { x: newX, y: newY };
    });
  }, [planeSize, userId]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'w':
          moveUser('up');
          break;
        case 's':
          moveUser('down');
          break;
        case 'a':
          moveUser('left');
          break;
        case 'd':
          moveUser('right');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [moveUser]);

  useEffect(() => {
    // Ensure the menu is always open on the Profile page on desktop
    if (activeTab === "Profile" && isDesktop) {
      setMenuOpen(true);
    }
  }, [activeTab, isDesktop]);

  const handleLogin = () => {
    axiosInstance.post('/api/authenticate', { username })
      .then(response => {
        if (response.status === 200) {
          const user = response.data;
          if (user && user.id) {
            setUserId(user.id);
            setUserPosition({ x: user.xpos, y: user.ypos });
            setIsAuthenticated(true);
            setError('');
          } else {
            setError('User object not found');
          }
        }
      })
      .catch(error => {
        console.error('Authentication error:', error);
        setError('User not found, consider signing up');
      });
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setError('');
    setUserId(null);
  };

  const handleSignUp = () => {
    // Handle sign-up logic here
    axiosInstance.post('/api/saveUser', { username, email })
      .then(response => {
        console.log('Sign-up successful:', response.data);
        setIsSignUp(false);
        setUsername('');
        setEmail('');
        setError('');
      })
      .catch(error => {
        console.error('There was an error signing up!', error);
        setError('Sign-up failed. Please try again.');
      });
  };

  const handlePostSubmit = (post) => {
    console.log('Post submitted:', post);
    // Handle post submission logic here
  };

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: 'var(--background-color)', color: 'var(--primary-color)', fontFamily: "'PressStart2P', sans-serif" }}>
        <div style={{ textAlign: 'center', width: '300px' }}>
          {isSignUp ? (
            <>
              <h2>Sign Up</h2>
              <div style={{ textAlign: 'left' }}>
                <label style={{ fontSize: '12px' }}>Username</label>
                <input type="text" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} style={{ padding: '10px', marginBottom: '10px', width: '100%', fontFamily: "'PressStart2P', sans-serif" }} />
                <label style={{ fontSize: '12px' }}>Email</label>
                <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ padding: '10px', marginBottom: '10px', width: '100%', fontFamily: "'PressStart2P', sans-serif" }} />
              </div>
              <button onClick={handleSignUp} style={{ padding: '10px 20px', margin: '10px 0', width: '100%', fontFamily: "'PressStart2P', sans-serif", backgroundColor: '#FF007F', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Sign Up</button>
              <button onClick={() => setIsSignUp(false)} style={{ padding: '10px 20px', width: '100%', fontFamily: "'PressStart2P', sans-serif", backgroundColor: '#333', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Back to Login</button>
            </>
          ) : (
            <>
              <h2>Login</h2>
              <input type="text" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} style={{ padding: '10px', marginBottom: '10px', width: '100%', fontFamily: "'PressStart2P', sans-serif" }} />
              <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ padding: '10px', marginBottom: '10px', width: '100%', fontFamily: "'PressStart2P', sans-serif" }} />
              {error && <p style={{ color: 'red' }}>{error}</p>}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={handleLogin} style={{ padding: '10px 20px', width: '48%', fontFamily: "'PressStart2P', sans-serif", backgroundColor: '#FF007F', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Login</button>
                <button onClick={() => setIsSignUp(true)} style={{ padding: '10px 20px', width: '48%', fontFamily: "'PressStart2P', sans-serif", backgroundColor: '#333', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Sign Up</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: "100vw", height: "100vh", overflow: "hidden", position: "relative", fontFamily: "'PressStart2P', sans-serif" }}>
      <style>
        {`
          body, html {
            overflow-x: hidden;
            font-family: 'PressStart2P', sans-serif;
          }
        `}
      </style>
      <div style={{ position: "absolute", top: 10, left: 10, zIndex: 1000 }}>
        <button onClick={() => setMenuOpen(!menuOpen)} style={{ padding: "10px", fontSize: "16px", background: "none", border: "none", cursor: "pointer", color: "var(--secondary-color)", fontFamily: "'PressStart2P', sans-serif" }}>
          ☰
        </button>
      </div>
      {menuOpen && <Menu setActiveTab={setActiveTab} setMenuOpen={setMenuOpen} handleLogout={handleLogout} />}
      {activeTab === "World" && (
        <>
          <MainContent planeSize={planeSize} viewportPosition={viewportPosition} backgroundImage={backgroundImage} userPosition={userPosition} users={users} visibleUsers={visibleUsers} />
          <NavigationButtons moveUser={moveUser} BOTTOM_TAB_HEIGHT={BOTTOM_TAB_HEIGHT} />
        </>
      )}
      {activeTab === "Profile" && (
        <Profile menuOpen={menuOpen} isDesktop={isDesktop} />
      )}
      {activeTab === "Updates" && <Updates visibleUsers={visibleUsers.map(user => user.id)} />}
      <FloatingButton onClick={() => setIsPostPopupOpen(true)} style={{ position: 'fixed', bottom: `${BOTTOM_TAB_HEIGHT + 20}px`, right: '20px', zIndex: 1000 }} />
      {isPostPopupOpen && <PostPopup onClose={() => setIsPostPopupOpen(false)} onSubmit={handlePostSubmit} />}
      <div style={{ position: "absolute", bottom: 0, width: "100%", display: "flex", justifyContent: "space-around", backgroundColor: "rgba(1, 0, 41, 0.8)", boxShadow: "0 -2px 10px #FF007F", height: `${BOTTOM_TAB_HEIGHT}px`, fontFamily: "'Orbitron', sans-serif" }}>
        <button onClick={() => { setActiveTab("World"); setMenuOpen(false); }} style={{ flexGrow: 1, height: "100%", padding: "10px 20px", fontSize: "20px", fontWeight: "bold", border: "none", background: activeTab === "World" ? "#2B2B2B" : "#101010", color: activeTab === "World" ? "#FF007F" : "#fff", transition: "background 0.3s", fontFamily: "'Orbitron', sans-serif" }}>World</button>
        <button onClick={() => { setActiveTab("Updates"); setMenuOpen(false); }} style={{ flexGrow: 1, height: "100%", padding: "10px 20px", fontSize: "20px", fontWeight: "bold", border: "none", background: activeTab === "Updates" ? "#2B2B2B" : "#101010", color: activeTab === "Updates" ? "#FF007F" : "#fff", transition: "background 0.3s", fontFamily: "'Orbitron', sans-serif" }}>Updates</button>
        <button onClick={handleLogout} style={{ flexGrow: 1, height: "100%", padding: "10px 20px", fontSize: "20px", fontWeight: "bold", border: "none", background: "#101010", color: "#fff", transition: "background 0.3s", fontFamily: "'Orbitron', sans-serif" }}>Logout</button>
      </div>
    </div>
  );
};

export default SocialMedia2DPOC;

